import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { generateReport, getClients, getBots, getActivityLogs } from '@/lib/api';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  CalendarIcon,
  BarChart3,
  TrendingUp,
  Users,
  Bot,
  Ticket,
  MessageSquare,
  Loader2,
  ShieldCheck,
  History,
  AlertTriangle,
  RefreshCw,
  User,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const ReportsPage = () => {
  const [clients, setClients] = useState([]);
  const [bots, setBots] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('insights');
  const [filters, setFilters] = useState({
    client_id: 'all',
    bot_id: 'all',
    start_date: null,
    end_date: null,
    report_type: 'summary'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, botsRes] = await Promise.all([
        getClients(),
        getBots()
      ]);
      setClients(clientsRes.data);
      setBots(botsRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await getActivityLogs();
      setActivityLogs(response.data);
    } catch (error) {
      toast.error('Error al cargar la bitácora');
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchLogs();
    }
  }, [activeTab]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const params = {
        report_type: filters.report_type
      };
      
      if (filters.client_id !== 'all') params.client_id = filters.client_id;
      if (filters.bot_id !== 'all') params.bot_id = filters.bot_id;
      if (filters.start_date) params.start_date = filters.start_date.toISOString();
      if (filters.end_date) params.end_date = filters.end_date.toISOString();

      const response = await generateReport(params);
      setReport(response.data);
      toast.success('Reporte generado exitosamente');
    } catch (error) {
      toast.error('Error al generar el reporte');
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;
    
    const reportJson = JSON.stringify(report, null, 2);
    const blob = new Blob([reportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Reporte descargado');
  };

  const getEventIcon = (type) => {
    if (type?.includes('delete')) return <AlertTriangle className="w-4 h-4 text-rose-500" />;
    if (type?.includes('create')) return <RefreshCw className="w-4 h-4 text-emerald-500" />;
    return <History className="w-4 h-4 text-violet-500" />;
  };

  const filteredBots = filters.client_id === 'all' 
    ? bots 
    : bots.filter(b => b.client_id === filters.client_id);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="h-64 rounded-xl bg-muted animate-pulse" />
          <div className="lg:col-span-2 h-64 rounded-xl bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Header V4.3 - ADN Fluxo Co */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Centro de Inteligencia</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
              Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Analytics</span>
            </h1>
            <p className="text-slate-500 mt-2 font-medium italic text-sm">Analítica avanzada, reportes ejecutivos y trazabilidad total de la red administrativa</p>
          </motion.div>
          
          <TabsList className="flex items-center gap-2 p-1 bg-slate-950/40 border border-white/5 rounded-2xl w-fit shadow-2xl backdrop-blur-xl">
            <TabsTrigger 
              value="insights" 
              className="rounded-xl px-6 py-2.5 flex items-center gap-2 font-black text-[10px] uppercase tracking-widest transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-blue-500/30 data-[state=active]:scale-[1.05]"
            >
              <TrendingUp className="w-4 h-4 transition-colors" /> ESTADÍSTICAS
            </TabsTrigger>
            <TabsTrigger 
              value="logs" 
              className="rounded-xl px-6 py-2.5 flex items-center gap-2 font-black text-[10px] uppercase tracking-widest transition-all duration-300 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-emerald-500/30 data-[state=active]:scale-[1.05]"
            >
              <ShieldCheck className="w-4 h-4 transition-colors" /> AUDITORÍA
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="insights" className="m-0 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Filters */}
            <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-outfit flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-500" />
                  Configuración
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select
                    value={filters.client_id}
                    onValueChange={(value) => setFilters({ ...filters, client_id: value, bot_id: 'all' })}
                  >
                    <SelectTrigger data-testid="report-client-select">
                      <SelectValue placeholder="Todos los clientes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los clientes</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Bot</Label>
                  <Select
                    value={filters.bot_id}
                    onValueChange={(value) => setFilters({ ...filters, bot_id: value })}
                  >
                    <SelectTrigger data-testid="report-bot-select">
                      <SelectValue placeholder="Todos los bots" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los bots</SelectItem>
                      {filteredBots.map((bot) => (
                        <SelectItem key={bot.id} value={bot.id}>
                          {bot.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal border-border/40",
                          !filters.start_date && "text-muted-foreground"
                        )}
                        data-testid="report-start-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.start_date ? format(filters.start_date, "PPP", { locale: es }) : "Seleccionar fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.start_date}
                        onSelect={(date) => setFilters({ ...filters, start_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal border-border/40",
                          !filters.end_date && "text-muted-foreground"
                        )}
                        data-testid="report-end-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.end_date ? format(filters.end_date, "PPP", { locale: es }) : "Seleccionar fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.end_date}
                        onSelect={(date) => setFilters({ ...filters, end_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button
                  data-testid="generate-report-btn"
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="w-full btn-glow mt-4 bg-violet-600 hover:bg-violet-700"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generar Reporte
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Report preview */}
            <div className="lg:col-span-2">
              {!report ? (
                <Card className="h-full flex items-center justify-center border-dashed border-2 bg-muted/10">
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-muted/30 mx-auto flex items-center justify-center mb-4">
                      <FileText className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                    <h3 className="text-lg font-medium mb-1 font-outfit">Sin reporte activo</h3>
                    <p className="text-sm text-muted-foreground max-w-sm px-4">
                      Configura los filtros y genera un reporte para visualizar la salud de tu red.
                    </p>
                  </div>
                </Card>
              ) : (
                <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
                  <CardHeader className="flex flex-row items-center justify-between border-b border-border/40 pb-4">
                    <div>
                      <CardTitle className="text-lg font-outfit">Vista Previa</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1 text-violet-400">
                        {format(new Date(report.generated_at), "dd MMMM yyyy, HH:mm", { locale: es })}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadReport}
                      data-testid="download-report-btn"
                      className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Exportar
                    </Button>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-6">
                    {/* Summary stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-xl border border-border/40 bg-muted/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="w-4 h-4 text-violet-500" />
                          <span className="text-xs text-muted-foreground font-medium">Bots</span>
                        </div>
                        <p className="text-2xl font-bold font-outfit">{report.summary.total_bots}</p>
                        <p className="text-xs text-emerald-500 font-medium">{report.summary.active_bots} Online</p>
                      </div>

                      <div className="p-4 rounded-xl border border-border/40 bg-muted/20">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare className="w-4 h-4 text-cyan-500" />
                          <span className="text-xs text-muted-foreground font-medium">Chats</span>
                        </div>
                        <p className="text-2xl font-bold font-outfit">{report.summary.total_conversations}</p>
                      </div>

                      <div className="p-4 rounded-xl border border-border/40 bg-muted/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Ticket className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs text-muted-foreground font-medium">Tickets</span>
                        </div>
                        <p className="text-2xl font-bold font-outfit">{report.summary.total_tickets}</p>
                        <p className="text-xs text-muted-foreground">{report.summary.completed_tickets} OK</p>
                      </div>

                      <div className="p-4 rounded-xl border border-border/40 bg-muted/20">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-amber-500" />
                          <span className="text-xs text-muted-foreground font-medium">Ratio</span>
                        </div>
                        <p className="text-2xl font-bold font-outfit">{report.summary.completion_rate}%</p>
                      </div>
                    </div>

                    {/* Revenue */}
                    <div className="relative overflow-hidden p-6 rounded-2xl border border-violet-500/30 bg-violet-600/[0.05]">
                      <div className="absolute top-0 right-0 p-4 opacity-10">
                        <TrendingUp className="w-24 h-24 text-violet-500" />
                      </div>
                      <p className="text-sm font-medium text-violet-400 mb-1">Impacto Económico Estimado</p>
                      <p className="text-4xl font-black font-outfit bg-clip-text text-transparent bg-gradient-to-r from-white to-violet-400">
                        ${report.summary.estimated_revenue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">Basado en flujos procesados y tickets completados.</p>
                    </div>

                    {/* Tickets by status */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold font-outfit flex items-center gap-2">
                        <div className="w-1 h-4 bg-violet-500 rounded-full" />
                        Tickets por Estado
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        {Object.entries(report.tickets_by_status).map(([status, count]) => (
                          <div key={status} className="p-3 rounded-xl border border-border/40 bg-muted/20 text-center">
                            <p className="text-lg font-black font-outfit">{count}</p>
                            <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                              {status === 'pending' ? 'Pendientes' :
                               status === 'in_progress' ? 'Proceso' :
                               status === 'completed' ? 'Éxito' : 'Cancel'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bots detail */}
                    {report.bots_detail && report.bots_detail.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold font-outfit flex items-center gap-2">
                          <div className="w-1 h-4 bg-cyan-500 rounded-full" />
                          Desempeño por Bot
                        </h4>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                          {report.bots_detail.map((bot) => (
                            <div key={bot.id} className="group flex items-center justify-between p-4 rounded-xl border border-border/40 bg-muted/10 hover:bg-muted/30 transition-all">
                              <div className="flex items-center gap-3">
                                <div className={cn(
                                  "w-2.5 h-2.5 rounded-full shadow-lg animate-pulse",
                                  bot.status === 'active' ? "bg-emerald-500 shadow-emerald-500/20" : "bg-amber-500 shadow-amber-500/20"
                                )} />
                                <div>
                                  <span className="font-bold text-sm block">{bot.name}</span>
                                  <span className="text-[10px] text-muted-foreground uppercase">{bot.id.substring(0,8)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <div className="text-right">
                                  <span className="text-xs font-bold block">{bot.conversations}</span>
                                  <span className="text-[9px] text-muted-foreground uppercase">Msjs</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xs font-bold block">{bot.tickets}</span>
                                  <span className="text-[9px] text-muted-foreground uppercase">Tks</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="m-0">
          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between border-b border-border/40 pb-4">
              <div>
                <CardTitle className="text-xl font-outfit flex items-center gap-2">
                  <ShieldCheck className="w-6 h-6 text-cyan-500" />
                  Bitácora de Auditoría
                </CardTitle>
                <p className="text-sm text-muted-foreground">Registro de todas las acciones administrativas críticas</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={fetchLogs} 
                disabled={loadingLogs}
                className="text-muted-foreground hover:text-cyan-400"
              >
                <RefreshCw className={cn("w-4 h-4", loadingLogs && "animate-spin")} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingLogs ? (
                <div className="py-20 flex flex-col items-center justify-center space-y-4">
                  <Loader2 className="w-10 h-10 text-cyan-500 animate-spin" />
                  <p className="text-muted-foreground font-outfit">Sincronizando bitácora...</p>
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="py-20 text-center">
                  <History className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium">Sin actividad reciente</h3>
                  <p className="text-sm text-muted-foreground">No se han registrado acciones administrativas en este periodo.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground bg-muted/10 border-b border-border/40">
                      <tr>
                        <th className="px-6 py-4">Evento</th>
                        <th className="px-6 py-4">Usuario</th>
                        <th className="px-6 py-4">Objetivo</th>
                        <th className="px-6 py-4" style={{ minWidth: "150px" }}>Fecha/Hora</th>
                        <th className="px-6 py-4 text-right">Origen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/20">
                      {activityLogs.map((log, index) => (
                        <tr key={log._id || index} className="group hover:bg-cyan-500/[0.02] transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-muted/30 flex items-center justify-center group-hover:bg-cyan-500/10 transition-colors">
                                {getEventIcon(log.event_type)}
                              </div>
                              <div>
                                <span className="font-bold block whitespace-nowrap">
                                  {log.event_type === 'admin_delete_tenant' ? 'Borrado de Tenant' : log.event_type}
                                </span>
                                <span className="text-[10px] text-muted-foreground uppercase tracking-tight">Acción Crítica</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-violet-500/10 flex items-center justify-center">
                                <User className="w-3 h-3 text-violet-400" />
                              </div>
                              <span className="text-xs font-medium text-muted-foreground">{log.actor_email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-violet-400">{log.target_tenant_name}</span>
                              <span className="text-[10px] text-muted-foreground font-mono">{log.target_tenant_id}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Clock className="w-3.5 h-3.5" />
                              <span className="text-xs whitespace-nowrap">{format(new Date(log.timestamp), "d MMM, HH:mm:ss", { locale: es })}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-muted/30 text-muted-foreground">
                              {log.ip || 'SYSTEM'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
            <CardHeader className="border-t border-border/40 pt-4 bg-muted/5">
              <p className="text-[10px] text-muted-foreground flex items-center gap-2">
                <ShieldCheck className="w-3 h-3 text-cyan-500" />
                Esta bitácora es inmutable y solo accesible por Super Administradores de Fluxo Co.
              </p>
            </CardHeader>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
